import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import * as THREE from "three";
import { BsChevronDoubleDown } from "react-icons/bs";
import "./Intro.css";
import Typewriter from "typewriter-effect";


const Intro = () => {
  const [showArrow, setShowArrow] = useState(true);
  const containerRef = useRef(null);
  const sceneRef = useRef(null);

  useEffect(() => {
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    sceneRef.current = { scene, camera, renderer };

    const container = containerRef.current;
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);

    // Create helix group with scale adjustment for mobile
    const helixGroup = new THREE.Group();
    
    // Adjust scale based on screen size ranges
    const getScale = () => {
      const width = window.innerWidth;
      const aspect = window.innerWidth / window.innerHeight;
      
      let baseScale;
      if (width <= 375) {
        baseScale = 0.65;
      } else if (width <= 480) {
        baseScale = 0.75;
      } else if (width <= 768) {
        baseScale = 0.85;
      } else if (width <= 900) {
        baseScale = 1.0;
      } else {
        baseScale = 1.0;
      }

      // Apply uniform scale
      return baseScale;
    };

    const scale = getScale();
    helixGroup.scale.set(scale, scale, scale);

    // Adjust camera position and field of view based on screen size
    const setupCamera = () => {
      const width = window.innerWidth;
      const aspect = window.innerWidth / window.innerHeight;
      
      if (width <= 768) {
        camera.position.z = 15;
        camera.fov = 60; // Adjusted field of view for mobile
      } else {
        camera.position.z = 12;
        camera.fov = 75; // Original field of view
      }
      
      camera.aspect = aspect;
      camera.updateProjectionMatrix();
    };

    setupCamera();

    // Parameters for the helix
    const points = 25;
    const radius = 2;
    const height = 8;
    const turns = 2;
    const sphereRadius = 0.12;

    // Create the strands
    const strand1 = new THREE.Group();
    const strand2 = new THREE.Group();
    
    // Sphere geometry for points
    const sphereGeometry = new THREE.SphereGeometry(sphereRadius, 8, 8);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: 0x2503ff,
      transparent: true,
      opacity: 0.8
    });

    // Line material for connections
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0x2503ff,
      transparent: true,
      opacity: 0.3
    });

    // Create points and connections
    for (let i = 0; i < points; i++) {
      const t = i / points;
      const angle = t * Math.PI * 2 * turns;

      // First strand
      const x1 = Math.cos(angle) * radius;
      const y1 = (t - 0.5) * height;
      const z1 = Math.sin(angle) * radius;

      // Second strand (offset by 180 degrees)
      const x2 = Math.cos(angle + Math.PI) * radius;
      const y2 = y1;
      const z2 = Math.sin(angle + Math.PI) * radius;

      // Create spheres for points
      const sphere1 = new THREE.Mesh(sphereGeometry, sphereMaterial.clone());
      sphere1.position.set(x1, y1, z1);
      strand1.add(sphere1);

      const sphere2 = new THREE.Mesh(sphereGeometry, sphereMaterial.clone());
      sphere2.position.set(x2, y2, z2);
      strand2.add(sphere2);

      // Create connecting lines between strands
      if (i % 2 === 0) {
        const connectionGeometry = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(x1, y1, z1),
          new THREE.Vector3(x2, y2, z2)
        ]);
        const line = new THREE.Line(connectionGeometry, lineMaterial);
        helixGroup.add(line);
      }
    }

    helixGroup.add(strand1);
    helixGroup.add(strand2);

    // Add data particles
    const particleCount = 50;
    const particles = new THREE.Group();
    const particleGeometry = new THREE.SphereGeometry(0.05, 8, 8);
    const particleMaterial = new THREE.MeshBasicMaterial({
      color: 0x2503ff,
      transparent: true,
      opacity: 0.4
    });

    for (let i = 0; i < particleCount; i++) {
      const particle = new THREE.Mesh(particleGeometry, particleMaterial.clone());
      particle.position.set(
        (Math.random() - 0.5) * 8,
        (Math.random() - 0.5) * 8,
        (Math.random() - 0.5) * 8
      );
      particle.userData = {
        speed: Math.random() * 0.02 + 0.01,
        offset: Math.random() * Math.PI * 2
      };
      particles.add(particle);
    }

    helixGroup.add(particles);
    scene.add(helixGroup);

    // Mouse interaction
    let mouseX = 0;
    let mouseY = 0;
    let targetX = 0;
    let targetY = 0;

    const handleMouseMove = (event) => {
      mouseX = (event.clientX / window.innerWidth) * 2 - 1;
      mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Animation
    let time = 0;
    const animate = () => {
      time += 0.01;
      targetX = mouseX * 0.5;
      targetY = mouseY * 0.3;
      
      helixGroup.rotation.y += 0.005;
      helixGroup.rotation.x += (targetY - helixGroup.rotation.x) * 0.05;

      // Animate strand points
      strand1.children.forEach((sphere, i) => {
        sphere.scale.setScalar(1 + Math.sin(time * 2 + i * 0.2) * 0.2);
        sphere.material.opacity = 0.6 + Math.sin(time * 2 + i * 0.2) * 0.2;
      });

      strand2.children.forEach((sphere, i) => {
        sphere.scale.setScalar(1 + Math.sin(time * 2 + i * 0.2 + Math.PI) * 0.2);
        sphere.material.opacity = 0.6 + Math.sin(time * 2 + i * 0.2 + Math.PI) * 0.2;
      });

      // Animate particles
      particles.children.forEach(particle => {
        particle.position.y += particle.userData.speed;
        particle.material.opacity = 0.2 + Math.sin(time * 2 + particle.userData.offset) * 0.2;
        if (particle.position.y > 4) particle.position.y = -4;
      });

      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };

    animate();

    // Update the resize handler
    const handleResize = () => {
      const scale = getScale();
      helixGroup.scale.set(scale, scale, scale);
      
      setupCamera();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      container.removeChild(renderer.domElement);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowArrow(false);
      } else {
        setShowArrow(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const mainText = "MusterTaker, The Smart Muster Solution for Ship Safety";
  const subText = "Effortlessly track crew during emergencies with real-time facial recognition and secure video connectivity across the ship.";
  
  const [displayMainText, setDisplayMainText] = useState("");
  const [displaySubText, setDisplaySubText] = useState("");
  const [mainTextComplete, setMainTextComplete] = useState(false);
  const [currentMainIndex, setCurrentMainIndex] = useState(0);
  const [currentSubIndex, setCurrentSubIndex] = useState(0);

  useEffect(() => {
    if (currentMainIndex < mainText.length) {
      const timeout = setTimeout(() => {
        setDisplayMainText(prev => prev + mainText[currentMainIndex]);
        setCurrentMainIndex(prev => prev + 1);
      }, 100); // Original typing speed for main text
      return () => clearTimeout(timeout);
    } else {
      setMainTextComplete(true);
    }
  }, [currentMainIndex, mainText]);

  useEffect(() => {
    if (mainTextComplete && currentSubIndex < subText.length) {
      const timeout = setTimeout(() => {
        setDisplaySubText(prev => prev + subText[currentSubIndex]);
        setCurrentSubIndex(prev => prev + 1);
      }, 50); // Original typing speed for sub text
      return () => clearTimeout(timeout);
    }
  }, [currentSubIndex, subText, mainTextComplete]);

  return (
    <section className="intro">
      <div className="text-container">
        <h1>
          <Typewriter
            options={{
              strings: [
                '<span class="title-highlight">MusterTaker</span>, The Smart Muster Solution for Ship Safety',
              ],
              autoStart: true,
              loop: false,
              cursor: "",
              delay:30,
              deleteSpeed: Infinity,
              html: true,
            }}
          />
        </h1>
        <div className="description">
          <Typewriter
            options={{
              strings: [
                "Effortlessly track crew during emergencies with real-time facial recognition and secure video connectivity across the ship.",
              ],
              autoStart: true,
              loop: false,
              cursor: "",
              delay: 16,
              deleteSpeed: Infinity,
            }}
          />
        </div>
      </div>

      <div ref={containerRef} className="three-container">
        {/* Three.js canvas will be inserted here */}
      </div>

      {showArrow && (
        <div className="scroll-arrow">
          <BsChevronDoubleDown size={30} />
        </div>
      )}
    </section>
  );
};

export default Intro;
